
import { Component } from 'vue-property-decorator';
import BasicDisplayerFilterable from '@/components/reports-v3/components/filterables/BasicDisplayerFilterable.vue';
import BaseLineChart from '@/components/reports-v3/components/elements/charts/BaseLineChart.vue';
import moment from 'moment';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import * as echarts from 'echarts';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		BasicDisplayerFilterable,
		BaseLineChart,
	},
})
export default class BpCashBankRatioDisplayer extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'year'
	> = ['year'];

	public seriesData: number[] = [];
	public xAxisData: string[] = [];
	public totalAverage: number = 0;

	public get series(): echarts.ECharts[] {
		const ratio: echarts.ECharts | any = {
			name: 'Value',
			type: 'line',
			data: this.seriesData,
		};

		const result: echarts.ECharts[] = [ratio];
		return result;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedDateRange } = this;
		return JSON.stringify([selectedAsOfDate, selectedDateRange]);
	}

	public async expensiveCalc() {
		const table = getTableName('transactions');
		const range = this.selectedDateRange;
		const dateAsOf = range[0] - 1;
		const caseCb = `SUM(CASE WHEN "ACCTYPE" = 'CA' AND "SPECIALACCTYPE" IN ('CH','BA') THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as cb`;
		const caseCl = `SUM(CASE WHEN "ACCTYPE" = 'CL' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as cl`;

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['transactions']);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		const [monthlyResult, asOfResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as year,
					EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as month,
					${caseCb}, ${caseCl}
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${range[0]} AND ${range[1]}
					AND "ACCTYPE" IN ('CA', 'CL')
				GROUP BY year, month ORDER BY year, month
			`),
			queryHead.rawQuery(`
				SELECT ${caseCb}, ${caseCl}
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${dateAsOf}
					AND "ACCTYPE" IN ('CA', 'CL')
			`),
		]);
		this.checkInterrupted(jobId);

		const monthlyMap: Record<string, any> = {};
		for (const row of monthlyResult) {
			monthlyMap[`${Number(row.year)}-${Number(row.month)}`] = row;
		}

		const startMoment = moment(range[0]).startOf('month');
		const endMoment = moment(range[1]).endOf('month');
		const labels: string[] = [];
		const monthlyRows: any[] = [];
		const mm = startMoment.clone();
		while (mm.isSameOrBefore(endMoment, 'month')) {
			labels.push(mm.format('MMM YYYY'));
			monthlyRows.push(monthlyMap[`${mm.year()}-${mm.month() + 1}`] || {});
			mm.add(1, 'month');
		}

		const currentMonth = moment().endOf('month');
		const isThisYear = range[1] > currentMonth.valueOf();
		const currentMonthIndex = isThisYear ? currentMonth.month() + 1 : 12;

		const a0 = asOfResult[0] || {};
		let asOfCashBank = Number(a0.cb) || 0;
		let asOfLiability = Number(a0.cl) || 0;

		const results: number[] = [];
		for (let i = 0; i < currentMonthIndex; i++) {
			const row = monthlyRows[i];
			asOfCashBank += Number(row.cb) || 0;
			asOfLiability += Number(row.cl) || 0;
			// Cash is debit (positive), Liability is credit (negative). Negate liability for ratio
			results[i] =
				roundToFixed(asOfLiability) !== 0
					? roundToFixed(asOfCashBank / (asOfLiability * -1))
					: 0;
		}
		this.seriesData = results;

		this.xAxisData = labels;

		this.totalAverage = roundToFixed(
			this.seriesData.reduce((a, b) => a + b, 0) / currentMonthIndex,
			gdbx.numDecimal,
		);

		this.saveHistory('xAxisData', 'seriesData', 'totalAverage');
	}
}
